<template>
  <div class="document">
    <div class="header">
      <EsHeader>
        <template #header>
          General Properties
        </template>
        <template #actions v-if="hasModule('SYS:SYS:CONFIGMGR:EDIT')">
          <ul>
            <li class="p-narrow no-border">
              <div class="list-content">
                <a role="button"
                  @keypress.enter.prevent="editPanel()"
                  @click="editPanel()"
                  :class="{ disabled: editOtherPanels }"
                  v-if="!editCurrentPanel"
                  :tabindex="editOtherPanels ? -1 : 0"
                >
                  <font-awesome-icon :icon="['far', 'edit']" size="lg" title="Edit" />
                </a>
              </div>
            </li>
          </ul>
        </template>
      </EsHeader>
    </div>
    <div class="content">
      <es-form style="padding: 1.15rem 0rem">
        <template #es-form-header>
          <div class="header-container">
            <div class="header-elements">
              <font-awesome-icon :icon="['fas', 'cog']" size="lg"/>
            </div>
            <strong class="title is-6">
              Display Properties
            </strong>
          </div>
        </template>
        <template #es-form>
          <table>
            <tbody>
              <tr>
                <td></td>
                <th class="property has-text-grey top-row table-header-normal">
                  ID Display Format
                </th>
                <td class="top-row">
                  <div class="control">
                    <input
                      class="input has-text-right is-pulled-right"
                      :class="{ 'is-static': !editCurrentPanel, 'is-danger' : errStateId }"
                      @keypress="checkInput($event)"
                      @keyup.prevent="validStateIdFormat(form.ID_DISPLAY_FORMAT)"
                      onPaste="return false"
                      :readonly="!editCurrentPanel"
                      type="text"
                      v-model="form.ID_DISPLAY_FORMAT"
                      :aria-label="form.ID_DISPLAY_FORMAT"
                    >
                  </div>
                  <span v-if="errStateId" class="help is-danger">
                    {{ msgStateId }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </template>
      </es-form>
      <es-form style="padding: 0rem 0rem 1.15rem 0rem">
        <template #es-form-header>
          <div class="header-container">
            <div class="header-elements">
              <font-awesome-icon :icon="['fas', 'cog']" size="lg"/>
            </div>
            <strong class="title is-6">
              Default Properties
            </strong>
          </div>
        </template>
        <template #es-form>
          <table>
            <tbody>
              <tr>
                <td></td>
                <th
                  class="property has-text-grey top-row table-header-normal"
                  :class="{ 'no-border': editCurrentPanel }"
                >
                  Extract: Downloadable Types Allowed
                  <span v-if="editCurrentPanel">
                    (optional)
                  </span>
                </th>
                <td class="top-row" :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <es-select
                      v-if="editCurrentPanel"
                      class="is-pulled-right"
                      v-on:update:values="extractDownloadAllowed = $event"
                      valueTpl="${description}"
                      :setValues="Object.values(extractDownloadOptions)"
                      keyValue="code"
                      :multi="true"
                      :preSelected="extractDownloadAllowed"
                    />
                    <div
                      v-else-if="extractDownloadAllowed.length"
                      class="tags is-pulled-right"
                      v-for="(item, index) in extractDownloadAllowed"
                      :key="index"
                    >
                      <strong class="tag source-tag">
                        {{ item.description }}
                      </strong>&nbsp;
                    </div>
                    <div v-else class="tags is-pulled-right">
                      <strong class="tag source-tag">
                        N/A
                      </strong>&nbsp;
                    </div>
                  </div>
                </td>
              </tr>
              <tr>
                <td></td>
                <th
                  class="property has-text-grey table-header-normal"
                  :class="{ 'no-border': editCurrentPanel }"
                >
                  Extract: Default Extract Format
                </th>
                <td :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <div
                      class="buttons has-addons is-pulled-right"
                      v-if="editCurrentPanel"
                    >
                      <button
                        class="button button--form"
                        v-for="(option, index) in sortedExtractDownloadAllowed"
                        :key="index"
                        @click="form.DEFAULT_EXTRACT_TEMPLATE = option.code"
                        :class="{ 'is-active': form.DEFAULT_EXTRACT_TEMPLATE === option.code }"
                      >
                        {{ extractDownloadOptions[option.code].description }}
                      </button>
                    </div>
                    <div v-else-if="sortedExtractDownloadAllowed.length" class="is-pulled-right">
                      {{ extractDownloadOptions[form.DEFAULT_EXTRACT_TEMPLATE].description }}
                    </div>
                    <div v-else class="is-pulled-right">
                      N/A
                    </div>
                  </div>
                </td>
              </tr>
              <tr>
                <td></td>
                <th
                  style="vertical-align: top"
                  class="property has-text-grey table-header-normal"
                  :class="{ 'no-border': editCurrentPanel }"
                >
                  FNS Report State Label
                  <span v-if="editCurrentPanel">
                    (optional)
                  </span>
                </th>
                <td :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <textarea
                      v-if="editCurrentPanel"
                      class="textbox"
                      rows="6"
                      v-model="form.FNS_REPORT_STATE_LABEL"
                      :aria-label="form.FNS_REPORT_STATE_LABEL"
                    />
                    <div v-else class="word--break has-text-right is-pulled-right">
                      {{ form.FNS_REPORT_STATE_LABEL }}
                    </div>
                  </div>
                </td>
              </tr>
              <tr>
                <td></td>
                <th
                  style="vertical-align: top"
                  class="property has-text-grey table-header-normal"
                  :class="{ 'no-border': editCurrentPanel }"
                >
                  FNS Report Contact Name
                  <span v-if="editCurrentPanel">
                    (optional)
                  </span>
                </th>
                <td :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <textarea
                      v-if="editCurrentPanel"
                      class="textbox"
                      rows="6"
                      v-model="form.FNS_REPORT_CONTACT_NAME"
                      :aria-label="form.FNS_REPORT_CONTACT_NAME"
                    />
                    <div v-else class="word--break has-text-right is-pulled-right">
                      {{ form.FNS_REPORT_CONTACT_NAME }}
                    </div>
                  </div>
                </td>
              </tr>
              <tr>
                <td></td>
                <th
                  style="vertical-align: top"
                  class="property has-text-grey table-header-normal"
                >
                  FNS Report Full Address
                  <span v-if="editCurrentPanel">
                    (optional)
                  </span>
                </th>
                <td>
                  <div class="control">
                    <textarea
                      v-if="editCurrentPanel"
                      class="textbox"
                      rows="6"
                      v-model="form.FNS_REPORT_FULL_ADDRESS"
                      :aria-label="form.FNS_REPORT_FULL_ADDRESS"
                    />
                    <div v-else class="word--break has-text-right is-pulled-right">
                      {{ form.FNS_REPORT_FULL_ADDRESS }}
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </template>
      </es-form>
    </div>
    <div
      v-if="editCurrentPanel"
      class="is-flex flex-justify--end flex-align--center"
      style="margin-right: 5.15rem; padding-bottom: 2rem"
    >
      <button
        class="button button-link has-underline"
        @click.prevent="cancelPanel()"
        style="margin-right: 1rem"
      >
        Cancel
      </button>
      <button
        class="button is-primary"
        @click.prevent="updatePanel()"
      >
        Update
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters, mapActions } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faEdit,
} from '@fortawesome/pro-regular-svg-icons';
import {
  faCog,
} from '@fortawesome/pro-solid-svg-icons';
import EsForm from '@/components/EsForm.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsSelect from '@/components/EsSelect.vue';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';

library.add(
  faCog,
  faEdit,
);

export default {
  mixins: [
    NotificationManager,
    PermissionManager,
  ],
  components: {
    EsForm,
    EsHeader,
    EsSelect,
  },
  props: {
    editOtherPanels: {
      type: Boolean,
    },
  },
  watch: {
    extractDownloadAllowed() {
      if (!this.extractDownloadAllowed.length) {
        this.form.DEFAULT_EXTRACT_TEMPLATE = null;
      } else if (this.extractDownloadAllowed.length !== 3) {
        this.form.DEFAULT_EXTRACT_TEMPLATE = this.extractDownloadAllowed[0].code;
      }
    },
  },
  data() {
    return this.initialData();
  },
  created() {
    this.extractDownloadOptions = {
      THREE: {
        code: 'THREE',
        description: '3.0',
      },
      THREEPLUS: {
        code: 'THREEPLUS',
        description: '3.0+',
      },
      DMINDEX: {
        code: 'DMINDEX',
        description: 'DM Index',
      },
    };
    this.initialize(this.properties);
  },
  mounted() {
    this.updateCache();
  },
  computed: {
    ...mapGetters('propertiesModule', [
      'properties',
    ]),
    sortedExtractDownloadAllowed() {
      function compare(a, b) {
        if (a.code < b.code) {
          return -1;
        }
        if (a.code > b.code) {
          return 1;
        }
        return 0;
      }
      return this.extractDownloadAllowed.slice().sort(compare).reverse();
    },
  },
  methods: {
    ...mapActions({
      initProperties: 'propertiesModule/init',
    }),
    cachedDownloadTypes() {
      this.extractDownloadAllowed = this.cachedExtractDownloadAllowed;
    },
    cancelPanel() {
      this.undoForm();
      this.cachedDownloadTypes();
      this.resetFormErrors();
      this.hideNotification();
      this.editCurrentPanel = false;
      this.$emit('allowEditOtherPanels');
    },
    checkForFormChanges() {
      return this.compareObjects(this.cache, this.form);
    },
    checkInput(evt) {
      // Listen to the key event when a keyboard key is pressed
      evt = (evt) || window.event; // eslint-disable-line
      // Store the ASCII value of the pressed key in 'charCode'
      const charCode = (evt.which) ? evt.which : evt.keyCode;
      // Prevent user from entering any values except 9 and (-)
      if (charCode !== 45 && charCode !== 57) {
        evt.preventDefault();
      }
    },
    compareObjects(o, n) {
      return JSON.stringify(o) !== JSON.stringify(n);
    },
    editPanel() {
      this.editCurrentPanel = true;
      this.$emit('allowEditOtherPanels');
    },
    formHasErrors() {
      return this.errStateId;
    },
    groupBySection(data, type) {
      return Object.entries(data)
        .filter(([key]) => key.includes(type))
        .map(([key, val]) => ({ [key.replace(type, '')]: val }));
    },
    initialize(data) {
      this.form = this.prepForDisplay('form', this.groupBySection(data, 'DIRECTMATCH-'));
      this.parseExtractDownloadTypesAllowed();
    },
    initialData() {
      return {
        editCurrentPanel: false,

        cache: {},
        form: {},

        extractDownloadAllowed: [],
        cachedExtractDownloadAllowed: [],
        extractDownloadOptions: {},

        errStateId: false,
        msgStateId: null,
      };
    },
    parseExtractDownloadTypesAllowed() {
      if (this.form.EXTRACT_DOWNLOAD_TYPES_ALLOWED != null) {
        this.extractDownloadAllowed = JSON.parse(this.form.EXTRACT_DOWNLOAD_TYPES_ALLOWED)
          .map(v => this.extractDownloadOptions[v]);
      } else {
        this.extractDownloadAllowed = [];
      }
      this.updateCache();
    },
    prepForDisplay(type, arrFromDb) {
      const o = {};
      arrFromDb.map((item) => {
        const key = Object.keys(item)[0];
        const value = item[key];
        o[key] = value;
        return 0;
      });
      return o;
    },
    prepForRequest() {
      const payload = [];
      let cached = null;
      let obj = null;
      let category = null;
      obj = this.form;
      cached = this.cache;
      category = 'DIRECTMATCH';
      Object.entries(obj).forEach(([property, value]) => {
        if (value !== cached[property]) {
          payload.push({ category, property, value });
        }
      });
      return payload;
    },
    resetFormErrors() {
      this.errStateId = false;
      this.msgStateId = null;
    },
    undoForm() {
      this.form = JSON.parse(JSON.stringify(this.cache));
    },
    updateCache() {
      this.cache = JSON.parse(JSON.stringify(this.form));
      this.cachedExtractDownloadAllowed = this.extractDownloadAllowed;
    },
    async updatePanel() {
      this.$emit('allowEditOtherPanels');
      if (!this.extractDownloadAllowed.length) {
        this.form.EXTRACT_DOWNLOAD_TYPES_ALLOWED = null;
      } else {
        this.form.EXTRACT_DOWNLOAD_TYPES_ALLOWED = JSON.stringify(
          this.extractDownloadAllowed.map(type => type.code),
        );
      }
      const changes = this.checkForFormChanges();
      if (!changes) {
        this.editCurrentPanel = false;
        this.hideNotification();
        return;
      }
      if (this.formHasErrors()) {
        this.$emit('allowEditOtherPanels');
        this.showNotification('danger', ['There were some errors. Please fix the errors highlighted in the fields below.']);
        return;
      }
      try {
        await axios({
          method: 'put',
          url: '/properties',
          data: this.prepForRequest(),
        });
        this.updateCache();
        await this.initProperties();
        this.showNotification('success', ['The General Properties have been updated successfully.']);
        this.editCurrentPanel = false;
      } catch (e) {
        this.$emit('allowEditOtherPanels');
        this.showNotification('danger', ['There was an issue. Please try again or contact your administrator.']);
      }
    },
    validStateIdFormat(userInput) {
      const regexSSN = (/^[^9]*(?:9[^9]*){10}$/gm);
      if (regexSSN.test(userInput)) {
        this.errStateId = false;
        this.msgStateId = null;
      } else {
        this.errStateId = true;
        this.msgStateId = 'State ID should be of 10 digits.';
      }
    },
  },
};
</script>

<style lang="sass" scoped>
  table td
    vertical-align: middle
    &:first-child
      border: 0
      width: 30px
  a.disabled
    color: #999
    cursor: default
    text-decoration: none
    pointer-events: none
    &:hover
      text-decoration: none
  .property
    padding-left: 0
    font-size: 12px
    width: 50%
  .top-row
    padding-top: 1rem
  .no-border
    border: 0
  .textbox
    border-color: #CED4DA
    width: 100%
    resize: vertical
  .word--break
    // width: 425px
    word-break: break-word
  .header-elements
    margin: 0px 7px !important
  .table-header-normal
    vertical-align: middle
</style>
